import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { saveTeamMember, type TeamListResponse, type TeamMember } from '../lib/bridge';
import { Button } from '../components/ui/Button';
import { ErrorState } from '../components/ui/ErrorState';
import { Card, CardHeader } from '../components/ui/Card';
import { fieldControlClass, fieldLabelClass } from '../components/ui/forms';

// Create/edit a team member's account, role, and permissions
// (docs/V6_ARCHITECTURE_AND_MIGRATION_PLAN.md §8, Stage 6). All validation
// — username format, duplicate check, the Owner-role-assignment
// restriction, primary-Owner protection, and role-appropriate permission
// floors/ceilings — happens server-side in the reused appSaveUser(); this
// form only shapes the request and shows the server's response, it does
// not duplicate or relax any of that.
export function TeamMemberForm({
  team,
  editing,
  onDone,
}: {
  team: TeamListResponse;
  editing: TeamMember | null;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(editing?.name ?? '');
  const [username, setUsername] = useState(editing?.username ?? '');
  const [email, setEmail] = useState((editing?.email as string) ?? '');
  const [role, setRole] = useState(editing?.role ?? 'FIELD_LEAD');
  const [notes, setNotes] = useState((editing?.notes as string) ?? '');
  const [permissions, setPermissions] = useState<Record<string, boolean>>((editing?.permissions as Record<string, boolean>) ?? team.rolePresets?.[role] ?? {});
  const [showPermissions, setShowPermissions] = useState(false);

  // New account: reset permission checkboxes to the chosen role's preset
  // whenever the role changes, so the form always starts from a sane
  // default rather than a stale role's checkboxes.
  useEffect(() => {
    if (!editing) setPermissions(team.rolePresets?.[role] ?? {});
  }, [role, editing, team.rolePresets]);

  const assignableRoles = useMemo(() => {
    const isOwner = team.currentUser?.role === 'OWNER';
    const roles = team.roles ?? [];
    return isOwner ? [{ value: 'OWNER', label: 'Owner' }, ...roles] : roles;
  }, [team.currentUser, team.roles]);

  const save = useMutation({
    mutationFn: () =>
      saveTeamMember({
        username,
        originalUsername: editing?.username,
        name,
        email,
        role,
        notes,
        permissions,
        // appSaveUser defaults a missing status to 'Active' (it's built for
        // the create path). Editing an already-Inactive account must
        // preserve that status explicitly, or an unrelated edit (fixing a
        // typo, changing a permission) would silently reactivate a
        // deactivated employee's account.
        status: (editing?.status as 'Active' | 'Inactive' | undefined) ?? 'Active',
      }),
    onSuccess: (result) => {
      if (result.ok) {
        void queryClient.invalidateQueries({ queryKey: ['team'] });
        if (!result.created) onDone();
      }
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || !username.trim()) return;
    save.mutate();
  }

  // A new account's temporary password is shown exactly once, in this
  // response — it is never retrievable again after this screen closes.
  if (save.data?.ok && save.data.created && save.data.temporaryPassword) {
    return (
      <Card className="border-success/30">
        <p className="font-semibold text-emerald-200">{name} was created.</p>
        <p className="mt-1.5 text-sm text-emerald-300/80">
          Temporary password (shown once — share it securely, it will be required to change on first login):
        </p>
        <code className="mt-2 block select-all rounded-lg border border-ink-border bg-ink px-3 py-2 text-sm font-mono text-white">{save.data.temporaryPassword}</code>
        <Button variant="secondary" onClick={onDone} className="mt-3">
          Done
        </Button>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title={editing ? `Edit ${editing.name}` : 'Add Team Member'} />
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <label htmlFor="tm-name" className={fieldLabelClass}>
              Name
            </label>
            <input id="tm-name" className={fieldControlClass} value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="space-y-1">
            <label htmlFor="tm-username" className={fieldLabelClass}>
              Username
            </label>
            <input id="tm-username" className={fieldControlClass} value={username} onChange={(e) => setUsername(e.target.value)} disabled={!!editing} required />
          </div>
          <div className="space-y-1">
            <label htmlFor="tm-email" className={fieldLabelClass}>
              Email
            </label>
            <input id="tm-email" type="email" className={fieldControlClass} value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="space-y-1">
            <label htmlFor="tm-role" className={fieldLabelClass}>
              Role
            </label>
            <select id="tm-role" className={fieldControlClass} value={role} onChange={(e) => setRole(e.target.value)}>
              {assignableRoles.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="space-y-1">
          <label htmlFor="tm-notes" className={fieldLabelClass}>
            Notes
          </label>
          <input id="tm-notes" className={fieldControlClass} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>

        <div>
          <button type="button" onClick={() => setShowPermissions((v) => !v)} className="text-sm font-semibold text-gold-300 hover:text-gold-200">
            {showPermissions ? 'Hide' : 'Customize'} permissions
          </button>
          {showPermissions && (
            <p className="mt-1.5 text-xs text-slate-500">
              Starts from {role}&rsquo;s standard permissions. Some permissions are always enforced by role regardless of this form (e.g. Field Lead can never create/waive contracts).
            </p>
          )}
          {showPermissions && (
            <div className="mt-2.5 grid grid-cols-2 gap-x-4 gap-y-2 rounded-lg border border-ink-border bg-ink p-3 sm:grid-cols-3">
              {(team.permissionCatalog ?? []).map((perm) => (
                <label key={perm.key} className="flex items-center gap-2 text-sm text-slate-300">
                  <input type="checkbox" checked={!!permissions[perm.key]} onChange={(e) => setPermissions((prev) => ({ ...prev, [perm.key]: e.target.checked }))} />
                  {perm.label}
                </label>
              ))}
            </div>
          )}
        </div>

        {save.data && !save.data.ok && <ErrorState message={save.data.message || 'Could not save this team member.'} />}
        {save.isError && <ErrorState message={save.error instanceof Error ? save.error.message : 'Could not save this team member.'} />}

        <div className="flex gap-2">
          <Button type="submit" busy={save.isPending} busyLabel="Saving…">
            {editing ? 'Save Changes' : 'Create Account'}
          </Button>
          <Button type="button" variant="secondary" onClick={onDone}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}
